import { useState, useEffect } from 'react';
import { FaSpinner, FaEdit, FaTrash, FaUserShield, FaPlus, FaRedo } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { Role } from '../../types/user';

const Groups = () => {
        const { token } = useAuth();
        const [groups, setGroups] = useState<Role[]>([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [searchTerm, setSearchTerm] = useState('');
        const [currentPage, setCurrentPage] = useState(1);
        const [showModal, setShowModal] = useState(false);
        const [selectedGroup, setSelectedGroup] = useState<Role | null>(null);
        const [permissions, setPermissions] = useState<Record<string, { view: boolean; add: boolean; change: boolean; delete: boolean }>>({});
        const itemsPerPage = 10;

        useEffect(() => {
                fetchGroups();
        }, [token]);

        const fetchGroups = async () => {
                if (!token) return;
                
                try {
                        setLoading(true);
                        setError(null);
                        const response = await apiCall(ENDPOINTS.GROUPS.LIST, { method: 'GET' }, token);
                        
                        if (!response.ok) {
                                throw new Error('Failed to fetch groups');
                        }
                        
                        const data = await response.json();
                        // Handle paginated response from Django REST Framework
                        const groupsData = data.results || data || [];
                        setGroups(groupsData);
                } catch (err) {
                        setError(err instanceof Error ? err.message : 'An error occurred');
                        console.error('Error fetching groups:', err);
                } finally {
                        setLoading(false);
                }
        };

        // Filter groups based on search term
        const filteredGroups = groups.filter((group) =>
                group.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Calculate pagination
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentGroups = filteredGroups.slice(indexOfFirstItem, indexOfLastItem);
        const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);

        const handlePageChange = (newPage: number) => {
                if (newPage >= 1 && newPage <= totalPages) {
                        setCurrentPage(newPage);
                }
        };

        const handleEditGroup = (group: Role) => {
                setSelectedGroup(group);
                
                // Parse existing permissions into matrix format
                const permissionMatrix: Record<string, { view: boolean; add: boolean; change: boolean; delete: boolean }> = {};
                const modules = ['Dictations', 'Scheduling', 'Billing', 'Letters', 'Clinical Information', 'Patient Demographics', 
                                'Documents', 'AI Integration', 'Email', 'Address Book', 'Research Studies', 'Publications', 
                                'Accounting', 'Facilities', 'Providers', 'User Management', 'Messaging'];
                
                modules.forEach(module => {
                        permissionMatrix[module] = { view: false, add: false, change: false, delete: false };
                });
                
                // If group has permissions, populate the matrix
                if (group.permissions && group.permissions.length > 0) {
                        group.permissions.forEach(perm => {
                                const action = perm.split('_')[0]; // view, add, change, delete
                                const modelName = perm.replace(/^(add|change|delete|view)_/, '');
                                
                                // Map model to module
                                const moduleMap: Record<string, string> = {
                                        'dictation': 'Dictations', 'dictationtype': 'Dictations', 'transcription': 'Dictations',
                                        'appointment': 'Scheduling', 'appointmenttype': 'Scheduling', 'session': 'Scheduling',
                                        'invoice': 'Billing', 'payment': 'Billing', 'billingcode': 'Billing',
                                        'letter': 'Letters', 'lettertemplate': 'Letters',
                                        'clinicalnote': 'Clinical Information', 'consultation': 'Clinical Information', 'procedure': 'Clinical Information',
                                        'patient': 'Patient Demographics', 'patientallergy': 'Patient Demographics',
                                        'document': 'Documents', 'documenttype': 'Documents',
                                        'ailettergeneration': 'AI Integration', 'aiprovider': 'AI Integration',
                                        'emailaccount': 'Email', 'correspondence': 'Email',
                                        'contact': 'Address Book',
                                        'researchstudy': 'Research Studies',
                                        'researchpublication': 'Publications',
                                        'chartofaccounts': 'Accounting',
                                        'facility': 'Facilities', 'room': 'Facilities',
                                        'provider': 'Providers',
                                        'user': 'User Management', 'group': 'User Management',
                                        'message': 'Messaging', 'messagethread': 'Messaging',
                                };
                                
                                const moduleName = moduleMap[modelName];
                                if (moduleName && permissionMatrix[moduleName]) {
                                        if (action === 'view') permissionMatrix[moduleName].view = true;
                                        if (action === 'add') permissionMatrix[moduleName].add = true;
                                        if (action === 'change') permissionMatrix[moduleName].change = true;
                                        if (action === 'delete') permissionMatrix[moduleName].delete = true;
                                }
                        });
                }
                
                setPermissions(permissionMatrix);
                setShowModal(true);
        };

        const handlePermissionChange = (module: string, permission: 'view' | 'add' | 'change' | 'delete', value: boolean) => {
                setPermissions(prev => ({
                        ...prev,
                        [module]: {
                                ...prev[module],
                                [permission]: value
                        }
                }));
        };

        const handleSavePermissions = async () => {
                if (!token || !selectedGroup) return;
                
                try {
                        // Convert permission matrix back to Django permission codenames
                        const permissionCodenames: string[] = [];
                        
                        // Map module names back to model names
                        const moduleToModelMap: Record<string, string[]> = {
                                'Dictations': ['dictation', 'dictationtype', 'transcription'],
                                'Scheduling': ['appointment', 'appointmenttype', 'appointmenttypecategory', 'session', 'sessiontype', 'waitinglist'],
                                'Billing': ['invoice', 'invoiceitem', 'payment', 'billingcode', 'billingtemplate', 'healthfund'],
                                'Letters': ['letter', 'lettertemplate'],
                                'Clinical Information': ['clinicalnote', 'consultation', 'diagnosis', 'procedure', 'proceduretype', 'outcome'],
                                'Patient Demographics': ['patient', 'patientallergy', 'emergencycontact', 'nextofkin'],
                                'Documents': ['document', 'documenttype', 'documentcategory'],
                                'AI Integration': ['ailettergeneration', 'aiprovider', 'researchquery'],
                                'Email': ['emailaccount', 'correspondence'],
                                'Address Book': ['contact', 'contactmethod'],
                                'Research Studies': ['researchstudy', 'researchparticipant'],
                                'Publications': ['researchpublication', 'publicationauthor'],
                                'Accounting': ['chartofaccounts'],
                                'Facilities': ['facility', 'facilitytype', 'room'],
                                'Providers': ['provider', 'providertype', 'providersession'],
                                'User Management': ['user', 'group', 'permission'],
                                'Messaging': ['message', 'messagethread']
                        };
                        
                        Object.entries(permissions).forEach(([module, perms]) => {
                                const models = moduleToModelMap[module] || [];
                                models.forEach(modelName => {
                                        if (perms.view) permissionCodenames.push(`view_${modelName}`);
                                        if (perms.add) permissionCodenames.push(`add_${modelName}`);
                                        if (perms.change) permissionCodenames.push(`change_${modelName}`);
                                        if (perms.delete) permissionCodenames.push(`delete_${modelName}`);
                                });
                        });
                        
                        // Update group permissions via API
                        const response = await apiCall(`${ENDPOINTS.GROUPS.LIST}${selectedGroup.id}/`, {
                                method: 'PATCH',
                                headers: {
                                        'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                        permissions: permissionCodenames
                                })
                        }, token);
                        
                        if (response.ok) {
                                // Refresh groups list
                                await fetchGroups();
                                setShowModal(false);
                                alert('Permissions updated successfully!');
                        } else {
                                const errorData = await response.json();
                                alert(`Failed to update permissions: ${errorData.detail || 'Unknown error'}`);
                        }
                } catch (err) {
                        console.error('Error saving permissions:', err);
                        alert('An error occurred while saving permissions');
                }
        };

        const getModulesFromPermissions = (permissions: string[] | undefined, groupName: string): string => {
                // Special case for Global Administrators
                if (groupName === 'Global Administrators') {
                        return 'Full Access - Everything';
                }
                
                if (!permissions || permissions.length === 0) {
                        return 'No permissions';
                }
                
                // Extract unique module names from permission codenames
                const modules = new Set<string>();
                
                permissions.forEach(permission => {
                        // Permission codename format is "action_modelname" (e.g., "add_dictation", "view_appointment")
                        // Strip the action prefix to get the model name
                        const modelName = permission.replace(/^(add|change|delete|view)_/, '');
                        
                        // Map model names to readable module names
                        const moduleMap: Record<string, string> = {
                                'dictation': 'Dictations',
                                'dictationtype': 'Dictations',
                                'transcription': 'Dictations',
                                'appointment': 'Scheduling',
                                'appointmenttype': 'Scheduling',
                                'appointmenttypecategory': 'Scheduling',
                                'session': 'Scheduling',
                                'sessiontype': 'Scheduling',
                                'waitinglist': 'Scheduling',
                                'invoice': 'Billing',
                                'invoiceitem': 'Billing',
                                'payment': 'Billing',
                                'billingcode': 'Billing',
                                'billingtemplate': 'Billing',
                                'healthfund': 'Billing',
                                'letter': 'Letters',
                                'lettertemplate': 'Letters',
                                'clinicalnote': 'Clinical Information',
                                'consultation': 'Clinical Information',
                                'diagnosis': 'Clinical Information',
                                'procedure': 'Clinical Information',
                                'proceduretype': 'Clinical Information',
                                'outcome': 'Clinical Information',
                                'patient': 'Patient Demographics',
                                'patientallergy': 'Patient Demographics',
                                'emergencycontact': 'Patient Demographics',
                                'nextofkin': 'Patient Demographics',
                                'document': 'Documents',
                                'documenttype': 'Documents',
                                'documentcategory': 'Documents',
                                'ailettergeneration': 'AI Integration',
                                'aiprovider': 'AI Integration',
                                'researchquery': 'AI Integration',
                                'emailaccount': 'Email',
                                'correspondence': 'Email',
                                'contact': 'Address Book',
                                'contactmethod': 'Address Book',
                                'researchstudy': 'Research Studies',
                                'researchparticipant': 'Research Studies',
                                'researchpublication': 'Publications',
                                'publicationauthor': 'Publications',
                                'chartofaccounts': 'Accounting',
                                'facility': 'Facilities',
                                'facilitytype': 'Facilities',
                                'room': 'Facilities',
                                'provider': 'Providers',
                                'providertype': 'Providers',
                                'providersession': 'Providers',
                                'user': 'User Management',
                                'group': 'User Management',
                                'permission': 'User Management',
                                'message': 'Messaging',
                                'messagethread': 'Messaging',
                        };
                        
                        const moduleName = moduleMap[modelName];
                        if (moduleName) {
                                modules.add(moduleName);
                        }
                });
                
                const moduleList = Array.from(modules).sort();
                return moduleList.length > 0 ? moduleList.join(', ') : 'Custom permissions';
        };

        const renderPermissionBadges = (permissions: string[] | undefined, groupName: string) => {
                const moduleText = getModulesFromPermissions(permissions, groupName);
                
                if (moduleText === 'No permissions') {
                        return <span className="text-base-content/40 text-sm">No permissions</span>;
                }
                
                if (moduleText === 'Full Access - Everything') {
                        return <span className="badge badge-success badge-sm">Full Access - Everything</span>;
                }
                
                // For regular groups, show the module list as plain text
                return <span className="text-sm">{moduleText}</span>;
        };

        if (loading) {
                return (
                        <div className="p-6">
                                <div className="flex justify-center items-center h-64">
                                        <FaSpinner className="animate-spin text-4xl text-primary" />
                                </div>
                        </div>
                );
        }

        if (error) {
                return (
                        <div className="p-6">
                                <div className="alert alert-error">
                                        <div className="flex justify-between items-center w-full">
                                                <span>Error loading groups: {error}</span>
                                                <button onClick={fetchGroups} className="btn btn-sm btn-ghost">
                                                        <FaRedo className="mr-2" /> Retry
                                                </button>
                                        </div>
                                </div>
                        </div>
                );
        }

        return (
                <div className="p-6">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaUserShield className="text-primary" />
                                        Groups
                                </h1>
                                <button className="btn btn-primary">
                                        <FaPlus className="mr-2" />
                                        Add New Group
                                </button>
                        </div>

                        {/* Filter Section */}
                        <div className="bg-base-200 p-4 rounded-lg mb-6">
                                <div className="flex items-center gap-4">
                                        <div className="form-control flex-1 max-w-md">
                                                <label className="label">
                                                        <span className="label-text font-semibold">Search Groups</span>
                                                </label>
                                                <input
                                                        type="text"
                                                        placeholder="Search by group name..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className="input input-bordered w-full bg-base-100"
                                                />
                                        </div>
                                </div>
                        </div>

                        {/* Groups Table */}
                        <div className="bg-base-100 rounded-lg shadow">
                                <div className="overflow-x-auto">
                                        <table className="table table-zebra w-full">
                                                <thead>
                                                        <tr>
                                                                <th>Group Name</th>
                                                                <th>Users</th>
                                                                <th>Permissions</th>
                                                                <th>Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        {currentGroups.length === 0 ? (
                                                                <tr>
                                                                        <td colSpan={4} className="text-center py-8 text-base-content/60">
                                                                                No groups found
                                                                        </td>
                                                                </tr>
                                                        ) : (
                                                                currentGroups.map((group) => (
                                                                        <tr key={group.id} className="hover">
                                                                                <td className="font-medium">{group.name}</td>
                                                                                <td>
                                                                                        <span className="badge badge-info badge-sm">
                                                                                                {group.user_count || 0} users
                                                                                        </span>
                                                                                </td>
                                                                                <td>
                                                                                        {renderPermissionBadges(group.permissions, group.name)}
                                                                                </td>
                                                                                <td>
                                                                                        <div className="flex gap-2">
                                                                                                <button onClick={() => handleEditGroup(group)} className="btn btn-ghost btn-xs">
                                                                                                        <FaEdit />
                                                                                                </button>
                                                                                                <button 
                                                                                                        className="btn btn-ghost btn-xs text-error"
                                                                                                        disabled={(group.user_count || 0) > 0}
                                                                                                        title={(group.user_count || 0) > 0 ? 'Cannot delete group with assigned users' : 'Delete group'}
                                                                                                >
                                                                                                        <FaTrash />
                                                                                                </button>
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                ))
                                                        )}
                                                </tbody>
                                        </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                        <div className="flex justify-center items-center gap-2 mt-6 pb-4 flex-shrink-0">
                                                <button
                                                        onClick={() => handlePageChange(1)}
                                                        disabled={currentPage === 1}
                                                        className="btn btn-sm"
                                                >
                                                        First
                                                </button>
                                                <button
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                        className="btn btn-sm"
                                                >
                                                        Previous
                                                </button>

                                                <span className="px-4">
                                                        Page {currentPage} of {totalPages}
                                                </span>

                                                <button
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                        className="btn btn-sm"
                                                >
                                                        Next
                                                </button>
                                                <button
                                                        onClick={() => handlePageChange(totalPages)}
                                                        disabled={currentPage === totalPages}
                                                        className="btn btn-sm"
                                                >
                                                        Last
                                                </button>
                                        </div>
                                )}
                        </div>

                        {/* Permission Matrix Modal */}
                        {showModal && selectedGroup && (
                                <div className="modal modal-open">
                                        <div className="modal-box max-w-4xl h-[80vh] flex flex-col">
                                                <h3 className="font-bold text-lg mb-4">Edit Permissions - {selectedGroup.name}</h3>
                                                
                                                {/* Scrollable Permission Matrix */}
                                                <div className="flex-1 overflow-y-auto">
                                                        <table className="table table-sm table-pin-rows">
                                                                <thead>
                                                                        <tr>
                                                                                <th className="w-48">Module</th>
                                                                                <th className="text-center">No Access</th>
                                                                                <th className="text-center">View Only</th>
                                                                                <th className="text-center">Edit</th>
                                                                                <th className="text-center">Delete</th>
                                                                        </tr>
                                                                </thead>
                                                                <tbody>
                                                                        {Object.keys(permissions).map(module => (
                                                                                <tr key={module}>
                                                                                        <td className="font-medium">{module}</td>
                                                                                        <td className="text-center">
                                                                                                <input 
                                                                                                        type="checkbox" 
                                                                                                        className="checkbox checkbox-sm" 
                                                                                                        checked={!permissions[module].view && !permissions[module].add && !permissions[module].change && !permissions[module].delete}
                                                                                                        onChange={(e) => {
                                                                                                                if (e.target.checked) {
                                                                                                                        handlePermissionChange(module, 'view', false);
                                                                                                                        handlePermissionChange(module, 'add', false);
                                                                                                                        handlePermissionChange(module, 'change', false);
                                                                                                                        handlePermissionChange(module, 'delete', false);
                                                                                                                }
                                                                                                        }}
                                                                                                />
                                                                                        </td>
                                                                                        <td className="text-center">
                                                                                                <input 
                                                                                                        type="checkbox" 
                                                                                                        className="checkbox checkbox-sm" 
                                                                                                        checked={permissions[module].view}
                                                                                                        onChange={(e) => handlePermissionChange(module, 'view', e.target.checked)}
                                                                                                />
                                                                                        </td>
                                                                                        <td className="text-center">
                                                                                                <input 
                                                                                                        type="checkbox" 
                                                                                                        className="checkbox checkbox-sm" 
                                                                                                        checked={permissions[module].change}
                                                                                                        onChange={(e) => handlePermissionChange(module, 'change', e.target.checked)}
                                                                                                />
                                                                                        </td>
                                                                                        <td className="text-center">
                                                                                                <input 
                                                                                                        type="checkbox" 
                                                                                                        className="checkbox checkbox-sm" 
                                                                                                        checked={permissions[module].delete}
                                                                                                        onChange={(e) => handlePermissionChange(module, 'delete', e.target.checked)}
                                                                                                />
                                                                                        </td>
                                                                                </tr>
                                                                        ))}
                                                                </tbody>
                                                        </table>
                                                </div>

                                                {/* Modal Actions - Fixed at bottom */}
                                                <div className="modal-action mt-4">
                                                        <button onClick={() => setShowModal(false)} className="btn">Cancel</button>
                                                        <button onClick={handleSavePermissions} className="btn btn-primary">Save Permissions</button>
                                                </div>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default Groups;